"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, Send, X, Users, ArrowRight, Shuffle, Check } from "lucide-react";

interface CouncilTile {
  id: string;
  name: string;
  type: string;
  profileName?: string;
}

interface PendingRound1 {
  question: string;
  tiles: { id: string; name: string }[];
  results: { tile: { id: string; name: string }; text: string }[];
}

interface Props {
  tiles: CouncilTile[];
  /** 2+ selected tiles deliberate off-screen and land on one collective
   *  verdict; a single tile is just a normal direct send. Awaited so the
   *  "forming first takes" spinner can clear on failure even when
   *  pendingRound1 never ends up getting set. */
  onAsk: (question: string, tileIds: string[]) => Promise<void>;
  /** Set once round 1 lands — each member's independent first answer,
   *  waiting for a steer pick (or "let them decide together") before round
   *  2 runs. Null the rest of the time. */
  pendingRound1?: PendingRound1 | null;
  /** tileId to steer round 2 toward, or null to let the council weigh
   *  every round-1 answer equally (the original, unsteered behavior). */
  onSteer: (steerTileId: string | null) => void;
  onClose: () => void;
  accent?: string;
}

export default function CouncilPanel({ tiles, onAsk, pendingRound1, onSteer, onClose, accent = "#C1A2FF" }: Props) {
  // Deliberately independent of top-bar tile linking (the chain icon,
  // which drives Multichat) — those are two unrelated concepts by design:
  // linking tiles at the top is "the main bar talks to all of them at
  // once", Council is its own separate roster you build here. Starting
  // this from linkedTileIds meant linking tiles for Multichat silently
  // pre-selected the exact same tiles here too, which read as "Council
  // getting triggered" every time — confirmed live by the user watching it
  // happen. Council always starts with nothing selected; every participant
  // requires its own deliberate tap here, independent of what's linked
  // elsewhere.
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [question, setQuestion] = useState("");
  const [waitingForRound1, setWaitingForRound1] = useState(false);
  const [lastAsked, setLastAsked] = useState<string | null>(null);

  useEffect(() => {
    if (pendingRound1) setWaitingForRound1(false);
  }, [pendingRound1]);

  const toggleTile = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const askCouncil = async () => {
    if (!question.trim() || selected.size === 0 || waitingForRound1 || pendingRound1) return;
    const ids = Array.from(selected);
    const asked = question.trim();
    setLastAsked(asked);
    setWaitingForRound1(true);
    setQuestion("");
    try {
      await onAsk(asked, ids);
    } finally {
      setWaitingForRound1(false);
    }
  };

  const selectAll = () => setSelected(new Set(tiles.map((t) => t.id)));
  const selectNone = () => setSelected(new Set());

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="fixed inset-x-0 bottom-0 z-50 flex items-end justify-center pointer-events-none"
    >
      <div
        className="w-full max-w-2xl pointer-events-auto rounded-t-2xl border-t border-x shadow-2xl max-h-[80vh] overflow-y-auto"
        style={{
          background: "rgba(17,18,27,0.96)",
          borderColor: "rgba(255,255,255,0.08)",
          backdropFilter: "blur(12px)",
        }}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 sticky top-0 z-10" style={{ background: "rgba(17,18,27,0.96)" }}>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4" style={{ color: accent }} />
            <span className="text-sm font-semibold text-white/90">Council / Roundtable</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 transition"
            aria-label="Close council"
          >
            <X className="w-4 h-4 text-white/60" />
          </button>
        </div>

        {pendingRound1 ? (
          <div className="px-4 py-4 space-y-3">
            <div>
              <p className="text-xs text-white/50 mb-1">The council's first take on</p>
              <p className="text-sm text-white/90">"{pendingRound1.question}"</p>
            </div>
            <p className="text-xs text-white/50">
              Pick one to steer the final answer toward — the rest will build on it. Or let them weigh in equally.
            </p>
            <div className="space-y-2">
              {pendingRound1.results.map((r) => (
                <button
                  key={r.tile.id}
                  type="button"
                  onClick={() => onSteer(r.tile.id)}
                  className="w-full text-left p-3 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/25 transition group"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium" style={{ color: accent }}>{r.tile.name}</span>
                    <span className="text-[10px] text-white/40 opacity-0 group-hover:opacity-100 transition flex items-center gap-1">
                      Steer this way <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                  <p className="text-xs text-white/70 leading-relaxed line-clamp-4">{r.text}</p>
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => onSteer(null)}
              className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl border border-white/10 text-xs text-white/60 hover:bg-white/5 hover:text-white/85 transition"
            >
              <Shuffle className="w-3 h-3" />
              Let them decide together
            </button>
          </div>
        ) : (
          <div className="px-4 py-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs text-white/50">Select participants</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={selectAll}
                  className="text-[10px] px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-white/70 transition"
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={selectNone}
                  className="text-[10px] px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-white/70 transition"
                >
                  None
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {tiles.map((tile) => {
                const isSelected = selected.has(tile.id);
                return (
                  // A border-color-only difference between selected and
                  // unselected read as nearly identical at a glance (both
                  // read as "a purple-ish outline") — confirmed live: even
                  // inspecting a screenshot pixel-by-pixel, the state was
                  // hard to call correctly despite the underlying toggle
                  // working every time. A filled background plus a
                  // checkmark makes the two states impossible to confuse.
                  <button
                    key={tile.id}
                    type="button"
                    onClick={() => toggleTile(tile.id)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-medium transition border ${
                      isSelected ? "border-transparent text-white" : "border-white/15 bg-white/5 text-white/40"
                    }`}
                    style={{ background: isSelected ? accent : undefined }}
                  >
                    {isSelected ? <Check className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}
                    <span className="truncate max-w-[120px]">
                      {tile.name || tile.profileName || tile.type}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="flex items-end gap-2">
              <div className="flex-1">
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Ask the council something…"
                  rows={2}
                  disabled={waitingForRound1}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white/90 placeholder:text-white/30 focus:outline-none focus:border-white/30 resize-none disabled:opacity-50"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      askCouncil();
                    }
                  }}
                />
              </div>
              <button
                type="button"
                onClick={askCouncil}
                disabled={!question.trim() || selected.size === 0 || waitingForRound1}
                className="shrink-0 p-3 rounded-xl transition disabled:opacity-40"
                style={{ background: accent }}
              >
                {waitingForRound1 ? (
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin block" />
                ) : (
                  <Send className="w-4 h-4 text-white" />
                )}
              </button>
            </div>

            {waitingForRound1 && (
              <p className="text-[11px] text-white/40">The council is forming its first takes…</p>
            )}

            <AnimatePresence>
              {!waitingForRound1 && lastAsked && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="text-[11px] text-white/40"
                >
                  Asked {selected.size} tile{selected.size === 1 ? "" : "s"}: “{lastAsked}”
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </motion.div>
  );
}
